import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Backdrop,
  Badge,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { grey, red } from "@mui/material/colors";
import { lighten } from "@mui/material/styles";
import { useAgentSessions } from "contexts/AgentSessions";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AgentSessionType } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";
import { routes } from "schemas/routes";

import TypingLoader from "features/Home/Sidebar/ChatHelper/TypingLoader";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";

import CartDialog from "./CartDialog";
import DefaultView from "./DefaultView";
import MessageContent from "./MessageContent";
import TemplatesDialog from "./TemplatesDialog";
import { styles } from "./styles";

export enum AgentSessionBrandStatus {
  in_cart = "in_cart",
  scheduled = "scheduled",
}

export interface AgentSessionBrand {
  bentoBrandId: number | null;
  googlePlaceBrandId: number | null;
  status: AgentSessionBrandStatus;
}

export interface BrandSearchResult {
  bento_brand_id: number | null;
  google_place_brand_id: number | null;
  source: string | null;
  name: string | null;
  website: string | null;
  instagram_username: string | null;
  description: string | null;
  address: string | null;
  rating: number | null;
  user_rating_count: number | null;
  google_maps_uri: string | null;
}

export enum PayloadType {
  brand_search_results = "brand_search_results",
  action_items = "action_items",
}

export enum Role {
  assistant = "assistant",
  user = "user",
  system = "system",
}

export interface ActionItemPreview {
  date: number;
  id: number;
  participants: string;
  snippet: string;
  subject: string;
  title: string;
  type: string;
}

export interface ActionItems {
  action_items: ActionItemPreview[];
}

export interface Message {
  id?: number;
  agent_session_id?: number;
  role: Role;
  text: string;
  suggested_responses?: string[];
  payload_type?: PayloadType;
  payload?: BrandSearchResult[] | ActionItems;
}

export default function Agent({ fetchLoading }: { fetchLoading: boolean }) {
  const [messages, setMessages] = useState<Message[]>([]);

  const [input, setInput] = useState("");
  const [loadingAddBentoBrand, setLoadingAddBentoBrand] = useState<Set<number>>(
    new Set(),
  );
  const navigate = useNavigate();
  const location = useLocation();
  const [loadingAddGooglePlaceBrand, setLoadingAddGooglePlaceBrand] = useState<
    Set<number>
  >(new Set());
  const [loadingChat, setLoadingChat] = useState(false);
  const [loading, setLoading] = useState(false);
  const [agentSessionBrands, setAgentSessionBrands] = useState<
    AgentSessionBrand[]
  >([]);
  const [bentoBrandResults, setBentoBrandResults] = useState<
    Map<number, BrandSearchResult>
  >(new Map());
  const [googlePlaceBrandResults, setGooglePlaceBrandResults] = useState<
    Map<number, BrandSearchResult>
  >(new Map());
  const [cartDialogOpen, setCartDialogOpen] = useState(false);
  const [templatesDialogOpen, setTemplatesDialogOpen] = useState(false);

  const chatEndRef = useRef<HTMLDivElement>(null);
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setErrorAlert } = useContext(AlertContext);
  const { selectedAgentSessionId, addNewAgentSession } = useAgentSessions();

  const updateBrandResults = (messages: Message[]) => {
    const updatedBentoBrandResults = new Map(bentoBrandResults);
    const updatedGooglePlaceBrandResults = new Map(googlePlaceBrandResults);
    for (const message of messages) {
      if (
        (message.payload as BrandSearchResult[]) &&
        message.payload_type === PayloadType.brand_search_results
      ) {
        for (const brandSearchResult of message.payload as BrandSearchResult[]) {
          if (brandSearchResult.bento_brand_id) {
            updatedBentoBrandResults.set(
              brandSearchResult.bento_brand_id,
              brandSearchResult,
            );
          }
          if (brandSearchResult.google_place_brand_id) {
            updatedGooglePlaceBrandResults.set(
              brandSearchResult.google_place_brand_id,
              brandSearchResult,
            );
          }
        }
      }
    }

    setBentoBrandResults(updatedBentoBrandResults);
    setGooglePlaceBrandResults(updatedGooglePlaceBrandResults);
  };

  const loadSavedBrands = async (sessionId: number | null) => {
    if (!sessionId) return;
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/agent-sessions/${sessionId}/brands`,
        "GET",
      );
      setAgentSessionBrands(res.agentSessionBrands || []);
    } catch (error) {}
  };

  const updateSavedBrands = async (
    action: "add" | "remove" | "replace",
    brandSearchResults: BrandSearchResult[],
  ) => {
    if (!selectedAgentSessionId) return;
    const loadingBentoBrandIds = brandSearchResults
      .filter((b) => b.bento_brand_id)
      .map((b) => b.bento_brand_id);
    const loadingGooglePlaceBrandIds = brandSearchResults
      .filter((b) => b.google_place_brand_id)
      .map((b) => b.google_place_brand_id);

    setLoadingAddBentoBrand((prev) => {
      const copy = new Set(prev);
      for (const _id of loadingBentoBrandIds) {
        if (_id) {
          copy.add(_id);
        }
      }
      return copy;
    });
    setLoadingAddGooglePlaceBrand((prev) => {
      const copy = new Set(prev);
      for (const _id of loadingGooglePlaceBrandIds) {
        if (_id) {
          copy.add(_id);
        }
      }
      return copy;
    });
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/agent-sessions/${selectedAgentSessionId}/brands`,
        "POST",
        {},
        {
          action,
          brandSearchResults,
        },
      );
      setAgentSessionBrands(res.agentSessionBrands || []);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoadingAddBentoBrand((prev) => {
        const copy = new Set(prev);
        for (const _id of loadingBentoBrandIds) {
          if (_id) {
            copy.delete(_id);
          }
        }
        return copy;
      });
      setLoadingAddGooglePlaceBrand((prev) => {
        const copy = new Set(prev);
        for (const _id of loadingGooglePlaceBrandIds) {
          if (_id) {
            copy.delete(_id);
          }
        }
        return copy;
      });
    }
  };

  const loadExistingSession = async (sessionId: number) => {
    try {
      setLoadingChat(true);
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/agent-sessions/${sessionId}`,
        "GET",
      );
      setMessages(res.messages || []);
      setBentoBrandResults(new Map());
      setGooglePlaceBrandResults(new Map());
      updateBrandResults(res.messages);
      setTimeout(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoadingChat(false);
    }
  };

  const initializeConversation = async (input: string) => {
    if (!input.trim()) return;
    setMessages((prev) => [...prev, { role: Role.user, text: input }]);
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });

    setLoading(true);

    let agentType = AgentSessionType.outreach;

    if (input.toLowerCase().includes("inbox")) {
      agentType = AgentSessionType.manage_inbox;
    } else if (input.toLowerCase().includes("suggestions")) {
      agentType = AgentSessionType.analytics;
    } else {
      input = "Heyyy";
    }

    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/agent-sessions/init`,
        "POST",
        {},
        {
          type: agentType,
          userInput: input,
        },
      );
      if (res.session) {
        updateBrandResults(res.session.items);
        setInput("");
        updateMessages(res.session.items);
        addNewAgentSession(res.session);
        navigate(`/${routes.home}/${res.session.id}${location.search}`, {
          replace: true,
        });
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitUserInput = async (input: string, sessionId?: number) => {
    if (!input.trim()) return;
    setMessages((prev) => [...prev, { role: Role.user, text: input }]);
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });

    setLoading(true);
    try {
      const { items } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/agent-sessions/${
          sessionId || selectedAgentSessionId
        }/run`,
        "POST",
        {},
        {
          userInput: input,
        },
      );
      updateBrandResults([items[items.length - 1]]);
      loadSavedBrands(sessionId || selectedAgentSessionId);
      setInput("");
      updateMessages(items);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  const updateMessages = (items: Message[]) => {
    if (!items) {
      return;
    }

    setMessages(items);
  };

  useEffect(() => {
    if (selectedAgentSessionId) {
      if (messages?.[0]?.agent_session_id !== selectedAgentSessionId) {
        loadExistingSession(selectedAgentSessionId);
        loadSavedBrands(selectedAgentSessionId);
      }
    } else if (!selectedAgentSessionId) {
      setMessages([]);
      setBentoBrandResults(new Map());
      setGooglePlaceBrandResults(new Map());
      setAgentSessionBrands([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAgentSessionId, currentOrg?.id]);

  const getSessionBrand = (brandSearchResult: BrandSearchResult) => {
    return agentSessionBrands.find(
      (b) =>
        (brandSearchResult.bento_brand_id &&
          brandSearchResult.bento_brand_id === b.bentoBrandId) ||
        (brandSearchResult.google_place_brand_id &&
          brandSearchResult.google_place_brand_id === b.googlePlaceBrandId),
    );
  };

  const addBrand = async (brand: BrandSearchResult) => {
    const addedBrand = getSessionBrand(brand);
    const action = !!addedBrand ? "remove" : "add";
    await updateSavedBrands(action, [brand]);
  };

  const handleScheduleEmails = () => {
    // TODO: validate that this is too many brands

    trackEvent("Agent Chat Cart Schedule Emails Button Pressed", {
      "Agent Session ID": selectedAgentSessionId,
      "Number of Brands": agentSessionBrands.filter(
        (agentSessionBrand) =>
          agentSessionBrand.status === AgentSessionBrandStatus.in_cart,
      ).length,
    });
    setCartDialogOpen(false);
    setTemplatesDialogOpen(true);
  };

  const overrideButtons = useMemo(() => {
    const lastMessage = messages[messages.length - 1];
    if (!lastMessage) {
      return null;
    }
    const responses = [];
    if (
      agentSessionBrands.filter(
        (sessionBrand) =>
          sessionBrand.status === AgentSessionBrandStatus.in_cart,
      ).length > 0
    ) {
      responses.push({
        text: "Schedule pitches",
        specialAction: "schedule",
      });
    }
    if (
      lastMessage.payload_type === PayloadType.brand_search_results &&
      lastMessage.payload
    ) {
      responses.push({
        text: "See more",
        specialAction: false,
      });
    }
    return responses.length > 0 ? responses : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentSessionBrands.length, messages.length]);

  if (messages.length === 0) {
    return (
      <DefaultView
        fetchLoading={fetchLoading}
        loadingChat={loadingChat}
        loading={loading}
        initializeConversation={initializeConversation}
        input={input}
        setInput={setInput}
      />
    );
  }

  const msg = messages[messages.length - 1];

  const savedBrands: BrandSearchResult[] = agentSessionBrands
    .filter(
      (agentSessionBrand) =>
        agentSessionBrand.status === AgentSessionBrandStatus.in_cart,
    )
    .map((agentSessionBrand) => {
      if (agentSessionBrand.bentoBrandId) {
        return bentoBrandResults.get(agentSessionBrand.bentoBrandId);
      } else if (agentSessionBrand.googlePlaceBrandId) {
        return googlePlaceBrandResults.get(
          agentSessionBrand.googlePlaceBrandId,
        );
      }
      return undefined;
    })
    .filter((result): result is BrandSearchResult => result !== undefined);

  const isBrandLoading = (brand: BrandSearchResult) => {
    return brand.bento_brand_id
      ? loadingAddBentoBrand.has(brand.bento_brand_id)
      : brand.google_place_brand_id
        ? loadingAddGooglePlaceBrand.has(brand.google_place_brand_id)
        : false;
  };

  return (
    <Box display="flex" flexDirection="column" height="calc(100vh - 60px)">
      {/* Messages container */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto", // 👈 scrolls
          px: 3,
          pt: 2,
          pb: { xs: 20, md: 30 },
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {loadingChat && (
          <Backdrop sx={{ zIndex: 9999 }} open={loadingChat}>
            <CircularProgress />
          </Backdrop>
        )}

        {messages.map((msg, i) => (
          <MessageContent
            key={i}
            msg={msg}
            addBrand={addBrand}
            getSessionBrand={getSessionBrand}
            isBrandLoading={isBrandLoading}
          />
        ))}

        <div ref={chatEndRef} />

        {loading && (
          <Typography
            component="div"
            variant="body2"
            color="text.secondary"
            ml={1}
          >
            <Paper
              elevation={0}
              sx={{
                py: 0.75,
                my: 1,
                bgcolor: "transparent",
                whiteSpace: "pre-wrap",
              }}
            >
              <TypingLoader title="Thinking" />
            </Paper>
          </Typography>
        )}
      </Box>

      {/* Footer (input + buttons) */}
      <Box
        sx={{
          borderTop: `1px solid ${grey[300]}`,
          backgroundColor: "white",
          px: 2,
          pt: 1,
          pb: { xs: "70px", md: "16px" },
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          columnGap={1}
          mb={2}
        >
          <Box>
            <Button
              size="small"
              onClick={() => {
                navigate(`/${routes.home}${location.search}`);
              }}
              sx={{ color: grey[500], mr: 2 }}
            >
              <Box
                component="span"
                className="fa-solid fa-plus"
                sx={{ mr: 1 }}
              />{" "}
              New Chat
            </Button>

            <Tooltip
              enterTouchDelay={0}
              title={
                savedBrands.length === 0
                  ? "Ask me for recommendations and add brands to this list using the 'Pitch +' button here"
                  : null
              }
            >
              <Box component="span">
                <Button
                  variant="outlined"
                  size="small"
                  disabled={savedBrands.length === 0}
                  sx={{
                    backgroundColor: (theme) =>
                      lighten(theme.palette.secondary.light, 0.6),
                  }}
                  onClick={() => setCartDialogOpen(true)}
                >
                  <Box sx={{ mr: 2 }}>
                    <Badge
                      badgeContent={savedBrands.length}
                      sx={{
                        "& .MuiBadge-badge": {
                          right: -3,
                          top: 7,
                          border: `2px solid white`,
                          padding: "0 4px",
                          backgroundColor: red[700],
                          color: "white",
                        },
                      }}
                    >
                      <Box
                        component="i"
                        className="fa solid fa-inbox"
                        fontSize="15px"
                        sx={{
                          color: (theme) =>
                            lighten(theme.palette.primary.main, 0.3),
                        }}
                      />
                    </Badge>
                  </Box>
                  Saved Brands
                </Button>
              </Box>
            </Tooltip>
          </Box>

          {msg?.suggested_responses && (
            <Box
              display="flex"
              justifyContent="flex-end"
              flexWrap="wrap"
              gap={1}
              sx={{ maxWidth: "75%" }}
            >
              {msg.suggested_responses.map((buttonText: string) => (
                <LoadingButton
                  key={buttonText}
                  disabled={loading}
                  size="small"
                  variant="outlined"
                  sx={styles.button}
                  onClick={() => handleSubmitUserInput(buttonText)}
                >
                  {buttonText}
                </LoadingButton>
              ))}
            </Box>
          )}

          {!msg?.suggested_responses && overrideButtons && (
            <Box
              display="flex"
              flexWrap="wrap"
              sx={{ maxWidth: "75%" }}
              justifyContent="flex-end"
              gap={1}
            >
              {overrideButtons.map(({ text, specialAction }) => (
                <LoadingButton
                  key={text}
                  disabled={loading}
                  size="small"
                  variant="outlined"
                  sx={styles.button}
                  onClick={() => {
                    if (!specialAction) {
                      handleSubmitUserInput(text);
                    } else {
                      setCartDialogOpen(true);
                    }
                  }}
                >
                  {text}
                </LoadingButton>
              ))}
            </Box>
          )}
        </Box>

        {/* Input */}
        <Box
          component="form"
          onSubmit={(e: CustomEvent) => {
            e.preventDefault();
            handleSubmitUserInput(input);
          }}
          display="flex"
          alignItems="center"
          gap={1}
        >
          <TextField
            value={input}
            onChange={(e) => setInput(e.target.value)}
            fullWidth
            placeholder="Respond here"
            disabled={loading}
            multiline
            minRows={1}
            maxRows={10}
            onKeyDown={(e: CustomEvent) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmitUserInput(e.target.value);
              }
            }}
            sx={styles.textField}
          />
          <IconButton
            type="submit"
            disabled={loading || !input.trim()}
            sx={{ width: 30, height: 30 }}
          >
            <Box component="span" className="fa-solid fa-paper-plane-top" />
          </IconButton>
        </Box>
      </Box>

      {templatesDialogOpen && (
        <TemplatesDialog
          open={templatesDialogOpen}
          handleClose={() => setTemplatesDialogOpen(false)}
          handleBack={() => {
            setTemplatesDialogOpen(false);
            setCartDialogOpen(true);
          }}
          handleSubmit={() => {
            handleSubmitUserInput("🗓️ Schedule pitches to selected brands");
          }}
          agentSessionId={selectedAgentSessionId}
        />
      )}

      <CartDialog
        open={cartDialogOpen}
        onClose={() => setCartDialogOpen(false)}
        savedBrands={savedBrands}
        isBrandLoading={isBrandLoading}
        addBrand={addBrand}
        onScheduleEmails={handleScheduleEmails}
      />
    </Box>
  );
}
